import type { ControlEvent } from '../../types/aai';
import type { ChipTone } from '../../components/aa';

/*
 * The one vocabulary for a team-scoped adoption action: what kind it is, where
 * it stands, who may move it and to what. The Department Head answers, the
 * person who raised it closes, and both pages read the same words.
 */

export const ACTION_KINDS = ['support_request', 'demonstration', 'blocker_assignment', 'capability_feedback', 'customization_request'];

/** The Owner's own requests: answered by a connection owner, an incident owner or platform operations. */
export const CONTROL_KINDS = ['access_review', 'scope_change_request', 'disconnect_request', 'incident_drill', 'evaluation_request'];

/** Every kind the response loop moves. */
export const RESPONDABLE_KINDS = [...ACTION_KINDS, ...CONTROL_KINDS];

export const ACTION_KIND_LABEL: Record<string, string> = {
  support_request: 'Support',
  demonstration: 'Demonstration',
  blocker_assignment: 'Blocker',
  capability_feedback: 'Quick Win feedback',
  customization_request: 'Customization',
  access_review: 'Access review',
  scope_change_request: 'Scope change',
  disconnect_request: 'Disconnect',
  incident_drill: 'Incident drill',
  evaluation_request: 'Capability evaluation',
};

/** Which page group an action belongs to, for the drawer's kicker. */
export const kickerFor = (kind: string): string => {
  if (CONTROL_KINDS.includes(kind)) return 'Controls';
  if (kind === 'capability_feedback' || kind === 'customization_request') return 'Quick Wins';
  return 'AI Adoption';
};

export const ACTION_STATUS: Record<string, { label: string; tone: ChipTone }> = {
  requested: { label: 'Requested', tone: 'warn' },
  sent: { label: 'Sent', tone: 'warn' },
  started: { label: 'Started', tone: 'warn' },
  recorded: { label: 'Recorded', tone: 'warn' },
  planned: { label: 'Planned', tone: 'warn' },
  scheduled: { label: 'Scheduled', tone: 'warn' },
  assigned: { label: 'Assigned', tone: 'warn' },
  accepted: { label: 'Accepted', tone: 'ok' },
  confirmed: { label: 'Confirmed', tone: 'ok' },
  acknowledged: { label: 'Acknowledged', tone: 'ok' },
  rescheduled: { label: 'Another time proposed', tone: 'warn' },
  delivered: { label: 'Delivered', tone: 'neutral' },
  held: { label: 'Held', tone: 'neutral' },
  resolved: { label: 'Resolved', tone: 'neutral' },
  declined: { label: 'Declined', tone: 'err' },
  not_ours: { label: 'Returned', tone: 'err' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  renewed: { label: 'Access renewed', tone: 'neutral' },
  reviewed: { label: 'Reviewed, no change', tone: 'neutral' },
  applied: { label: 'Applied', tone: 'neutral' },
  disconnected: { label: 'Disconnected', tone: 'neutral' },
  completed: { label: 'Completed', tone: 'neutral' },
  approved: { label: 'Approved', tone: 'ok' },
};

export const statusOf = (status: string): { label: string; tone: ChipTone } => ACTION_STATUS[status] || { label: status, tone: 'neutral' };

const AWAITING = new Set(['requested', 'sent', 'started', 'recorded', 'planned', 'scheduled', 'assigned']);
const CLOSED = new Set(['delivered', 'held', 'resolved', 'declined', 'not_ours', 'cancelled', 'renewed', 'reviewed', 'applied', 'disconnected', 'completed', 'approved']);

/** Nobody has answered yet. */
export const isAwaiting = (e: ControlEvent): boolean => AWAITING.has(e.status);
export const isClosed = (e: ControlEvent): boolean => CLOSED.has(e.status);
export const isOpenAction = (e: ControlEvent): boolean => ACTION_KINDS.includes(e.kind) && !CLOSED.has(e.status);
/** Any kind the loop moves, still open. */
export const isOpenRespondable = (e: ControlEvent): boolean => RESPONDABLE_KINDS.includes(e.kind) && !CLOSED.has(e.status);

export interface StepOption { status: string; label: string; needsNote: boolean; slot?: boolean }

/** What the people it was sent to may answer. */
export const RECIPIENT_STEPS: Record<string, StepOption[]> = {
  support_request: [
    { status: 'accepted', label: 'Accept the slot', needsNote: false },
    { status: 'rescheduled', label: 'Propose another time', needsNote: false, slot: true },
    { status: 'declined', label: 'Decline with a reason', needsNote: true },
  ],
  demonstration: [
    { status: 'confirmed', label: 'Confirm the session', needsNote: false },
    { status: 'rescheduled', label: 'Propose another time', needsNote: false, slot: true },
    { status: 'declined', label: 'Decline with a reason', needsNote: true },
  ],
  blocker_assignment: [
    { status: 'acknowledged', label: 'Acknowledge, we are on it', needsNote: false },
    { status: 'resolved', label: 'Mark resolved', needsNote: true },
    { status: 'not_ours', label: 'Not ours to remove', needsNote: true },
  ],
  capability_feedback: [
    { status: 'acknowledged', label: 'Acknowledge, we are looking at it', needsNote: false },
    { status: 'resolved', label: 'Resolved, say what changed', needsNote: true },
    { status: 'declined', label: 'Decline with a reason', needsNote: true },
  ],
  customization_request: [
    { status: 'acknowledged', label: 'Acknowledge, we are on it', needsNote: false },
    { status: 'delivered', label: 'Delivered, say what changed', needsNote: true },
    { status: 'declined', label: 'Decline with a reason', needsNote: true },
  ],
  access_review: [
    { status: 'renewed', label: 'Access renewed or adjusted', needsNote: true },
    { status: 'reviewed', label: 'Reviewed, nothing to change', needsNote: false },
    { status: 'declined', label: 'Decline with a reason', needsNote: true },
  ],
  scope_change_request: [
    { status: 'acknowledged', label: 'Acknowledge, we are on it', needsNote: false },
    { status: 'applied', label: 'Scope change applied', needsNote: true },
    { status: 'declined', label: 'Decline with a reason', needsNote: true },
  ],
  disconnect_request: [
    { status: 'disconnected', label: 'Disconnected', needsNote: false },
    { status: 'declined', label: 'Decline with a reason', needsNote: true },
  ],
  incident_drill: [
    { status: 'acknowledged', label: 'Acknowledge, drill is scheduled', needsNote: false },
    { status: 'completed', label: 'Drill completed, record the outcome', needsNote: true },
    { status: 'declined', label: 'Decline with a reason', needsNote: true },
  ],
  evaluation_request: [
    { status: 'acknowledged', label: 'Acknowledge, evaluation queued', needsNote: false },
    { status: 'completed', label: 'Evaluation completed, record the result', needsNote: true },
    { status: 'declined', label: 'Decline with a reason', needsNote: true },
  ],
};

const WITHDRAW: StepOption[] = [{ status: 'cancelled', label: 'Withdraw', needsNote: true }];

/** What the person who raised it may do. */
export const RAISER_STEPS: Record<string, StepOption[]> = {
  support_request: [
    { status: 'delivered', label: 'Mark delivered', needsNote: false },
    { status: 'cancelled', label: 'Cancel', needsNote: true },
  ],
  demonstration: [
    { status: 'held', label: 'Mark held', needsNote: false },
    { status: 'cancelled', label: 'Cancel', needsNote: true },
  ],
  blocker_assignment: WITHDRAW,
  capability_feedback: WITHDRAW,
  customization_request: WITHDRAW,
  access_review: WITHDRAW,
  scope_change_request: WITHDRAW,
  disconnect_request: WITHDRAW,
  incident_drill: [
    { status: 'completed', label: 'Drill completed, record the outcome', needsNote: true },
    { status: 'cancelled', label: 'Cancel', needsNote: true },
  ],
  evaluation_request: WITHDRAW,
};

const lower = (value: unknown): string => String(value || '').trim().toLowerCase();

export const sentTo = (e: ControlEvent): string[] => {
  const raw = (e.payload || {}).sent_to;
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => String(x));
};

export const raisedBy = (e: ControlEvent): string => e.actor_email || 'Not recorded';

/** What the action is about when it is not a team: a connection, a skill, a drill scenario. */
export const subjectOf = (e: ControlEvent): string => {
  const p = e.payload || {};
  if (typeof p.skill_name === 'string' && p.skill_name) return p.skill_name;
  if (typeof p.scenario === 'string' && p.scenario) return p.scenario;
  return e.subject.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

export const teamOf = (e: ControlEvent): string | null => {
  const value = (e.payload || {}).department;
  if (typeof value === 'string' && value) return value;
  return null;
};

export const payloadText = (e: ControlEvent, key: string): string | null => {
  const value = (e.payload || {})[key];
  if (typeof value === 'string' && value) return value;
  return null;
};

export type ActionSide = 'raiser' | 'recipient' | 'reader';

export const sideOf = (e: ControlEvent, me: string): ActionSide => {
  if (lower(e.actor_email) === lower(me)) return 'raiser';
  if (sentTo(e).some((x) => lower(x) === lower(me))) return 'recipient';
  return 'reader';
};

export const stepsFor = (e: ControlEvent, me: string): StepOption[] => {
  if (isClosed(e)) return [];
  const side = sideOf(e, me);
  if (side === 'recipient') return RECIPIENT_STEPS[e.kind] || [];
  if (side === 'raiser') return RAISER_STEPS[e.kind] || [];
  return [];
};

/** The team-scoped actions that concern one team, newest first. */
export const actionsForTeam = (events: ControlEvent[], team: string): ControlEvent[] => events
  .filter((e) => ACTION_KINDS.includes(e.kind) && lower(teamOf(e)) === lower(team));

export const latestOpenFor = (events: ControlEvent[], team: string): ControlEvent | null => actionsForTeam(events, team)
  .find((e) => !isClosed(e)) || null;

export const shortName = (email: string | null | undefined): string => {
  if (!email) return 'someone';
  return email.split('@')[0];
};
